export function applyFilter(records, filterTree) {
    return records.filter(r => evaluateNode(filterTree, r));
}

export function evaluateNode(node, record) {
    if (node && Array.isArray(node.rules)) {
        const cond = String(node.condition || "AND").toUpperCase();
        const rules = node.rules;

        if (rules.length === 0) return true;

        if (cond === "OR") {
            for (const child of rules) {
                if (evaluateNode(child, record)) return true;
            }
            return false;
        }

        for (const child of rules) {
            if (!evaluateNode(child, record)) return false;
        }
        return true;
    }

    return evaluateRule(node, record);
}

export function evaluateRule(rule, record) {
    if (!rule || !rule.field || !rule.operator) return true;

    const operator = rule.operator;
    const field = rule.field;

    const leftRaw = record.get(field);
    const left = coerce(leftRaw, rule.type);

    if (operator === "is_null") return isNullish(leftRaw);
    if (operator === "is_not_null") return !isNullish(leftRaw);

    const rightParsed = parseRuleValue(rule.value);
    const right = coerceRight(rightParsed, rule.type, operator);

    switch (operator) {
        case "equal":
            return compare(left, right, rule.type) === 0;

        case "not_equal":
            return compare(left, right, rule.type) !== 0;

        case "less":
            return compare(left, right, rule.type) < 0;

        case "less_or_equal":
            return compare(left, right, rule.type) <= 0;

        case "greater":
            return compare(left, right, rule.type) > 0;

        case "greater_or_equal":
            return compare(left, right, rule.type) >= 0;

        case "between": {
            if (!Array.isArray(right) || right.length !== 2) return false;
            const [minV, maxV] = right;
            return compare(left, minV, rule.type) >= 0 && compare(left, maxV, rule.type) <= 0;
        }

        default:
            throw new Error(`Unsupported operator: ${operator}`);
    }
}

export function isNullish(v) {
    return v === null || v === undefined;
}

export function parseRuleValue(v) {
    if (v === null || v === undefined) return v;

    if (typeof v === "string") {
        const s = v.trim();
        try {
            const obj = JSON.parse(s);
            if (obj && typeof obj === "object" && "value" in obj) return obj.value;
            return obj;
        } catch {
            return v;
        }
    }

    if (typeof v === "object" && "value" in v) return v.value;
    return v;
}

export function coerceRight(rightParsed, type, operator) {
    if (operator === "between") {
        if (Array.isArray(rightParsed)) {
            return [coerce(rightParsed[0], type), coerce(rightParsed[1], type)];
        }
        if (rightParsed && typeof rightParsed === "object" && ("from" in rightParsed || "to" in rightParsed)) {
            return [coerce(rightParsed.from, type), coerce(rightParsed.to, type)];
        }
        if (typeof rightParsed === "string") {
            const parts = rightParsed.split(",").map(x => x.trim());
            if (parts.length >= 2) return [coerce(parts[0], type), coerce(parts[1], type)];
        }
        return rightParsed;
    }

    return coerce(rightParsed, type);
}

export function coerce(value, type) {
    if (value === null || value === undefined) return value;

    switch (type) {
        case "integer": {
            const n = typeof value === "number" ? value : Number.parseInt(String(value), 10);
            return Number.isNaN(n) ? null : n;
        }
        case "double": {
            const n = typeof value === "number" ? value : Number.parseFloat(String(value));
            return Number.isNaN(n) ? null : n;
        }
        case "boolean": {
            if (typeof value === "boolean") return value;
            const s = String(value).trim().toLowerCase();
            if (s === "true" || s === "1") return true;
            if (s === "false" || s === "0") return false;
            return null;
        }
        case "datetime":
        case "date": {
            if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
            const d = new Date(value);
            return isNaN(d.getTime()) ? null : d;
        }
        case "string":
        default:
            return String(value);
    }
}

export function compare(a, b, type) {
    if (a === null || a === undefined) return NaN;
    if (b === null || b === undefined) return NaN;

    if (type === "integer" || type === "double") {
        const an = Number(a), bn = Number(b);
        if (Number.isNaN(an) || Number.isNaN(bn)) return NaN;
        return an === bn ? 0 : an < bn ? -1 : 1;
    }

    if (type === "boolean") {
        const av = !!a, bv = !!b;
        return av === bv ? 0 : av < bv ? -1 : 1;
    }

    if (type === "date" || type === "datetime") {
        const at = a instanceof Date ? a.getTime() : new Date(a).getTime();
        const bt = b instanceof Date ? b.getTime() : new Date(b).getTime();
        if (Number.isNaN(at) || Number.isNaN(bt)) return NaN;
        return at === bt ? 0 : at < bt ? -1 : 1;
    }

    const as = String(a), bs = String(b);
    return as === bs ? 0 : as < bs ? -1 : 1;
}
